import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Caption1, Dialog, InputTime, SelectTrigger } from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import moment from "moment";
import PropTypes from "prop-types";
import { getClosestOccurrence, isAllDay, isRepeating } from "~/components/Calendar/calendarUtils.js";
import TextareaProgress from "~/components/Inputs/TextareaProgress";
import { SubscribersAvatarList } from "~/components/SubscribersAvatarList/SubscribersAvatarList";
import { SubscribersPicker } from "~/components/tasks/TaskPanel/subscribers/SubscribersPicker";
import { useTickerContext } from "~/components/Ticker/context/index.js";
import { getPersonalizedTimeFormat, getUserTimezone } from "~/store/selectors/selector.configOptions.jsx";
import { getReminderById } from "~/store/selectors/selector.reminders";
import { validationSchema } from "~/validators/validate.reminders";
import { RelativeReminderOffset } from "./RelativeReminderOffset.js";
import { ReminderType } from "./ReminderType.js";
import useAcSelectDate from "../../Hooks/useAcSelectDate.jsx";
import SelectDateNonWorkingDays from "../../SelectDate/SelectDateNonWorkingDays.jsx";
import { showErrorToast } from "../../Toast";
import { DEFAULT_TIME, RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES } from "../utils.js";

export const GeneralReminderForm = ({
  id,
  parent,
  relatedField,
  recipients,
  nextWorkingDay,
  onCancel = () => null,
  onSubmit = () => null,
  members = [],
  open = false,
}) => {
  const { now } = useTickerContext();
  const { t } = useTranslation();
  const formikRef = useRef();
  const reminder = useSelector((state) => getReminderById(state, id));
  const { today } = useAcSelectDate();
  const formatTime = useSelector(getPersonalizedTimeFormat);
  const [showErrors, setShowErrors] = useState(false);
  const timezone = useSelector(getUserTimezone);

  const relativeRemindersEnabled = useMemo(() => {
    if (RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES.includes(parent.class) && relatedField in parent) {
      if (parent.class === "Task") {
        return parent[relatedField] > moment().unix() + 15 * 60;
      }
      if (parent.class === "CalendarEvent") {
        return moment(getClosestOccurrence(parent, timezone)).unix() > moment().unix() + 15 * 60;
      }
    }
    return false;
  }, [parent, relatedField, timezone]);

  const [isTodaySelected, setIsTodaySelected] = useState(
    reminder
      ? moment().format("YYYY-MM-DD") ===
          moment
            .unix(reminder.remind_on ?? reminder.send_on)
            .utc()
            .format("YYYY-MM-DD")
      : relativeRemindersEnabled
      ? moment().format("YYYY-MM-DD") === moment.unix(parent[relatedField]).utc().format("YYYY-MM-DD")
      : false
  );

  const parentIsHiddenFromClients = useMemo(() => {
    if ("is_hidden_from_clients" in parent) {
      return parent.is_hidden_from_clients;
    }
    return false;
  }, [parent]);

  const memberIds = useMemo(() => members.map((m) => m.id), [members]);

  const minTime = useMemo(() => {
    if (isTodaySelected && now > 0) {
      const min = moment().add(5, "minutes");
      const minutes = min.minutes();
      const roundedMinutes = Math.ceil(minutes / 5) * 5;

      if (roundedMinutes === 60) {
        min.add(1, "hour").minutes(0);
      } else {
        min.minutes(roundedMinutes);
      }

      return min.format("HH:mm");
    }
    return "00:00";
  }, [isTodaySelected, now]);

  const handleChangeSubscribers = useCallback(
    (value) => {
      let subscribers;
      const clientIds = members.reduce((prev, next) => {
        if (next.class === "Client") {
          prev.push(next.id);
        }
        return prev;
      }, []);
      if (parentIsHiddenFromClients) {
        subscribers = value.filter((v) => !clientIds.includes(v));
      } else {
        subscribers = value;
      }
      formikRef.current?.setFieldValue("subscribers", subscribers);
    },
    [members, parentIsHiddenFromClients]
  );

  const defaultRelativeValue = useMemo(() => {
    if (relativeRemindersEnabled) {
      if (reminder && typeof reminder.relative_value !== "undefined") {
        return Math.abs(reminder?.relative_value);
      }
      if (parent?.class === "Task") {
        return 0;
      }
      if (parent?.class === "CalendarEvent") {
        if (isAllDay(parent)) {
          return 0;
        } else {
          return 15;
        }
      }
    }
    return null;
  }, [parent, relativeRemindersEnabled, reminder]);

  const defaultRelativeUnit = useMemo(() => {
    if (relativeRemindersEnabled) {
      if (reminder) {
        return reminder?.relative_unit;
      }
      if (parent?.class === "Task") {
        return "day";
      }
      if (parent?.class === "CalendarEvent") {
        if (isAllDay(parent)) {
          return "day";
        } else {
          return "minute";
        }
      }
    }
    return null;
  }, [parent, relativeRemindersEnabled, reminder]);

  const defaultSendOn = useMemo(() => {
    if (reminder) {
      return moment
        .utc(
          moment
            .unix(reminder.remind_on ?? reminder.send_on)
            .startOf("day")
            .format("YYYY-MM-DD HH:mm:ss")
        )
        .unix();
    }
    if (parent?.class === "CalendarEvent") {
      if (moment().unix() < parent.starts_on) {
        return moment.utc(moment.unix(parent.starts_on).startOf("day").format("YYYY-MM-DD HH:mm:ss")).unix();
      }
    }
    return nextWorkingDay;
  }, [nextWorkingDay, parent?.class, parent.starts_on, reminder]);

  const handleSubmit = useCallback(
    async ({
      send_on,
      send_at,
      subscribers,
      is_relative,
      relative_unit,
      relative_value,
      relative_value_modifier,
      comment,
    }) => {
      const attributes = {
        subscribers,
        comment,
      };

      let remindOn;

      if (!is_relative) {
        const date = moment.unix(send_on).utc().format("YYYY-MM-DD");
        const time = `${send_at}:00`;

        remindOn = moment(`${date} ${time}`);
        attributes.remind_on = remindOn.utc().format();
      } else {
        let date;

        if (parent.class === "Task") {
          date = moment
            .unix(parent[relatedField])
            .utc()
            .add(relative_value * relative_value_modifier, relative_unit);
        }
        if (parent.class === "CalendarEvent") {
          if (isRepeating(parent)) {
            const from = moment();
            if (relative_value * relative_value_modifier <= 0) {
              if (relative_unit === "day") {
                let parts = send_at.split(":");
                from.set("hour", parts[0]);
                from.set("minute", parts[1]);
                from.set("second", 0);
                if (from.unix() <= moment().unix()) {
                  from.add(1, "day");
                }
                from.startOf("day");
              }

              from.add(relative_value, relative_unit);
            }
            const recurrenceInstance = getClosestOccurrence(parent, timezone, from.format("YYYY-MM-DD HH:mm:ss"), true);
            if (!recurrenceInstance) {
              return setShowErrors(true);
            }
            date = moment(recurrenceInstance);
            date.utc().add(relative_value * relative_value_modifier, relative_unit);
          } else {
            date = moment
              .unix(parent[relatedField])
              .utc()
              .add(relative_value * relative_value_modifier, relative_unit);
          }
        }
        if (relative_unit === "day") {
          const time = `${send_at}:00`;
          remindOn = moment(`${date.format("YYYY-MM-DD")} ${time}`);
          attributes.relative_utc_time = remindOn.utc().format("HH:mm");
        } else {
          remindOn = moment(`${date.format()}`);
        }

        attributes.is_relative = is_relative;
        attributes.relative_unit = relative_unit;
        attributes.relative_value = 0;
        if (Math.abs(relative_value)) {
          attributes.relative_value = relative_value * relative_value_modifier;
        }
      }

      if (remindOn.unix() <= moment().unix()) {
        return setShowErrors(true);
      }
      return onSubmit(attributes);
    },
    [onSubmit, parent, relatedField, timezone]
  );

  return (
    <Dialog
      open={open}
      enableBackgroundClick
      onClose={onCancel}
    >
      <Formik
        innerRef={formikRef}
        onSubmit={handleSubmit}
        initialValues={{
          send_on: defaultSendOn,
          send_at: reminder && reminder.remind_on ? moment.unix(reminder.remind_on).format("HH:mm") : DEFAULT_TIME,
          subscribers: reminder ? reminder.subscribers : recipients,
          comment: reminder && reminder.comment ? reminder.comment : "",
          is_today_selected: isTodaySelected,
          is_relative: relativeRemindersEnabled ? (reminder ? reminder?.is_relative : true) : false,
          relative_unit: defaultRelativeUnit,
          relative_value: defaultRelativeValue,
          relative_value_modifier: relativeRemindersEnabled
            ? reminder && reminder?.relative_value
              ? reminder?.relative_value >= 0
                ? 1
                : -1
              : -1
            : null,
        }}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, dirty, errors, values, handleSubmit: formikSubmit, setValues }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();
            formikSubmit();
            if (Object.keys(errors)?.length) {
              setShowErrors(true);
              if (Object.keys(errors)?.length === 1 && errors?.subscribers) {
                showErrorToast(t("Reminders need to have at least one subscriber."));
              }
            }
          };
          return (
            <Form onSubmit={handleFormSubmit}>
              <Dialog.Title className="modal_dialog_header">
                {reminder ? t("Edit Reminder") : t("Set Reminder")}
              </Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content className="modal_dialog_content object_reminder">
                {relativeRemindersEnabled ? (
                  <div className="tw-mb-6">
                    <ReminderType
                      parent={parent}
                      sendOn={values.send_on}
                      isRelative={values.is_relative}
                      relativeUnit={values.relative_unit}
                      relativeValue={values.relative_value}
                      onChange={(data) => {
                        setValues({ ...values, ...data });
                        setIsTodaySelected(data.is_today_selected);
                      }}
                    />
                  </div>
                ) : null}
                <div className="tw-flex tw-gap-2 tw-items-start sm:tw-items-end tw-flex-col sm:tw-flex-row">
                  {!values.is_relative ? (
                    <Field name="send_on">
                      {({ field }) => {
                        return (
                          <div className="tw-w-full sm:tw-w-32 tw-flex tw-flex-col">
                            <Caption1
                              className="tw-truncate tw-mb-1"
                              weight="bold"
                            >
                              {t("Date")}
                            </Caption1>
                            <SelectDateNonWorkingDays
                              trigger={(text) => <SelectTrigger className="tw-max-w-full">{text}</SelectTrigger>}
                              required
                              selectedDays={{ from: field.value, to: field.value }}
                              selectionMode="daily"
                              onSave={({ from }) => {
                                setFieldValue("send_on", from);
                                if (moment().format("YYYY-MM-DD") === moment.unix(from).utc().format("YYYY-MM-DD")) {
                                  setIsTodaySelected(true);
                                  setFieldValue("is_today_selected", true);
                                } else {
                                  setIsTodaySelected(false);
                                  setFieldValue("is_today_selected", false);
                                }
                              }}
                              popperClassName="z-index-1300"
                              backgroundElementClass="z-index-1300"
                              popperTooltipClassName="z-index-1300"
                              disableDaysBefore={today.clone().startOf("day")}
                              invalid={showErrors && errors?.send_on}
                              weekendIsSelectable
                            />
                          </div>
                        );
                      }}
                    </Field>
                  ) : values.relative_value || values.relative_unit === "minute" ? (
                    <div className="tw-flex tw-flex-col tw-w-full sm:tw-w-fit">
                      <Caption1
                        className="tw-truncate tw-mb-1"
                        weight="bold"
                      >
                        {t("When")}
                      </Caption1>
                      <RelativeReminderOffset
                        showErrors={showErrors}
                        relatedField={relatedField}
                        parent={parent}
                        relativeValue={values.relative_value}
                        relativeUnit={values.relative_unit}
                        relativeValueModifier={values.relative_value_modifier}
                        onChange={(data) => {
                          setValues({ ...values, ...data });
                          setIsTodaySelected(data.is_today_selected);
                        }}
                      />
                    </div>
                  ) : null}
                  {values.is_relative && values.relative_unit === "day" && values.relative_value !== 0 ? (
                    <Caption1 className="tw-truncate tw-h-8 tw-flex tw-items-center tw-hidden sm:tw-flex">
                      {t("at")}
                    </Caption1>
                  ) : null}
                  {(values.is_relative && values.relative_unit === "day") || !values.is_relative ? (
                    <Field name="send_at">
                      {({ field }) => {
                        return (
                          <div className="tw-w-full sm:tw-w-32 tw-flex tw-flex-col">
                            <Caption1
                              className="tw-truncate tw-mb-1"
                              weight="bold"
                            >
                              {t("Time")}
                            </Caption1>
                            <InputTime
                              className="tw-flex-1 tw-max-w-full"
                              selected={field.value}
                              min={minTime}
                              max="23:45"
                              onChange={(time) => {
                                setFieldValue("send_at", time);
                              }}
                              step={5}
                              mode={formatTime === "HH:mm" ? "24" : "12"}
                              addCustomTimeLabel={t("Add")}
                              invalid={showErrors && errors?.send_at}
                              placeholder={moment(field.value, "HH:mm").format(formatTime)}
                              hideClearButton
                            />
                          </div>
                        );
                      }}
                    </Field>
                  ) : null}
                </div>
                <Field name="subscribers">
                  {({ field }) => {
                    return (
                      <div className="tw-flex tw-mb-6 tw-mt-6 tw-items-center tw-h-6">
                        <Caption1
                          className="tw-truncate tw-mr-6"
                          weight="bold"
                        >
                          {t("Who will be notified")}
                        </Caption1>
                        <SubscribersPicker
                          onChange={handleChangeSubscribers}
                          hideClients={parentIsHiddenFromClients}
                          userIds={memberIds}
                          selected={field.value}
                          target={<SubscribersAvatarList subscriberIds={field.value} />}
                        />
                      </div>
                    );
                  }}
                </Field>
                <Caption1
                  className="tw-truncate tw-mr-6"
                  weight="bold"
                >
                  {t("Comment")}
                </Caption1>
                <Field name="comment">
                  {({ field, form }) => (
                    <TextareaProgress
                      id="comment"
                      className="tw-w-full"
                      textareaClassName="tw-resize-none tw-w-full tw-h-[96px] tw-mt-1"
                      value={field.value}
                      onChange={(e) => form.setFieldValue("comment", e.target.value)}
                      maxNumChars={190}
                      maxReachedClass="circle-progress--error"
                      warningClass="circle-progress--warning"
                    />
                  )}
                </Field>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  variant="primary"
                  className="tw-mr-3"
                  type="submit"
                  disabled={!dirty && reminder}
                >
                  {t("Save")}
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={onCancel}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

GeneralReminderForm.propTypes = {
  id: PropTypes.number,
  parent: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  relatedField: PropTypes.string,
  recipients: PropTypes.array.isRequired,
  nextWorkingDay: PropTypes.number,
  members: PropTypes.array,
  open: PropTypes.bool,
};
